import { AuthConsumerRenderProps, withAuthContext } from "auth/AuthContext";
import axios, { CancelTokenSource } from "axios";
import { getPatientDocumentFileUrl } from "components/clinical-data/documentUtils";
import PatientDocumentComments, {
  PatientMedicalDocumentCommentDtoExtended,
  prepareComments,
  prepareUpsertCommentsDtos
} from "components/clinical-data/PatientDocumentComments";
import DropZone from "components/dropzone/DropZone";
import CheckBox from "components/final-form/CheckBox";
import DatePicker from "components/final-form/DatePicker";
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithTextGetter
} from "components/final-form/dropdownUtils";
import Input from "components/final-form/Input";
import Select from "components/final-form/Select";
import FormButtonsRow from "components/layout/FormButtonsRow";
import FormContainer from "components/layout/FormContainer";
import FormContent from "components/layout/FormContent";
import PraxFormGrid from "components/layout/PraxFormGrid";
import PraxFormScrollableContainer from "components/layout/PraxFormScrollableContainer";
import StyledErrorMessage from "components/layout/StyledErrorMessage";
import LoaderComponent from "components/LoaderComponent";
import PatientMedicalDocumentPdfModal from "components/pdf-modal/PatientMedicalDocumentPdfModal";
import QrcodeActionButton from "components/QrcodeActionButton";
import { FormApi } from "final-form";
import arrayMutators from "final-form-arrays";
import moment from "moment";
import React, { Component } from "react";
import { FileRejection } from "react-dropzone";
import { Field, Form as FinalForm, FormRenderProps } from "react-final-form";
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Grid, Header, Icon, Loader, Progress } from "semantic-ui-react";
import {
  addCdrDocument,
  addFilesToDraft,
  createCdrDocumentDraft,
  createCdrDocumentDraftBase64String,
  deleteCdrDocumentDraft,
  deleteCdrDocumentDraftFile,
  editCdrDocument,
  getCdrDocumentDraft,
  getCdrDocumentDraftFile,
  getCdrDocumentFile
} from "services/cdrDocumentServices";
import { getAllDoctors } from "services/doctorServices";
import { getAllPatientDocumentTypes } from "services/patientDocumentTypeServices";
import { getPatientMedicalDocumentComments } from "services/patientRecordServices";
import { getUserDisplay } from "services/userSettingsServices";
import styled from "styled-components";
import { PatientDocumentOwnerEntity, Privilege } from "ts-types/api.enums";
import {
  CdrDocumentDraftDto,
  CdrDocumentDraftFileDto,
  CdrDocumentFileContent,
  CdrImageUploadLinkCreationResponse,
  PatientDocumentTypeDto,
  PatientDto,
  PatientMedicalDocumentFileDto,
  UpsertPatientMedicalDocumentCommentDto,
  UpsertPatientMedicalDocumentDto,
  UserDisplayDto
} from "ts-types/api.types";
import { isoToDisplayDate } from "utils/dateUtils";
import { getText } from "utils/textUtils";

const StyledSpan = styled.span`
  display: flex;
  margin-top: 10px;

  .show-hide-comments {
    cursor: pointer;
    color: blue;
    padding-left: 1rem;
  }
`;

const FileName = styled.div`
  display: inline-flex;
  align-items: center;
  width: 100%;
`;

const FileActionButton = styled.div`
  width: 1.5em;
`;

const btnStyle = {
  background: "transparent",
  cursor: "pointer",
  height: "2px"
};

const QrContentDiv = styled.div`

  min-height: 320px;
  padding: 0.2rem;

  .employee-data {
    font-size: 1.1rem;
    font-weight: 500;
    margin: 0 0 0.75rem 0.2rem;
    white-space: nowrap;
  }

  .qrcode-wrapper {
    display: inline-block;
    border: 1px solid #e5e5e5;
    padding: 1rem;
    margin-bottom: 0.5rem;

    .qrcode {
      background-size: contain;
      width: 140px;
      height: 140px;
    }
  }

  .actions-row {
    margin: 0.75rem 0;

    .ui.button {
      border-radius: unset;
    }
  }

  .link-info {
    color: #407A8C;
    margin: 0.75rem 0.1rem 0;
    padding-top: 0.5rem;
    border-top: 1px solid #e5e5e5;
  }
`;

const SnapshotButtonContainer = styled.div`
  border-left: 1px solid rgba(34, 36, 38, .15);
  padding: 20px;
  text-align: center;
`;

const StyledTriggerContainer = styled.div`
  cursor: pointer;
`;

const _100MB_in_bytes = 104857600;

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
  onSave: (successfullyUploaded: boolean, documentId: number | undefined) => void;
  onCancel: () => void;
  patientDocument?: UpsertPatientMedicalDocumentDto;
  patientId: number;
  documentId?: number;
  ownerEntityId?: number;
  ownerEntity?: PatientDocumentOwnerEntity;
  patient: PatientDto;
  field: string;
  fields: any;
}

interface State {
  draft?: CdrDocumentDraftDto,
  doctorOptions: DropdownOption[];
  documentTypeOptions: DropdownOption[];
  requiredDataLoaded: boolean;
  savingDocument?: boolean;
  documentComments: Partial<PatientMedicalDocumentCommentDtoExtended>[];
  currentUserDisplay: UserDisplayDto;
  errorMessage?: String;
  cancelTokenSource: CancelTokenSource;
  addingComment: boolean;
  editingComments: boolean;
  initialFormValues: Partial<UpsertPatientMedicalDocumentDto>;
  showDeletedComments: boolean;
  cdrImageUploadLinkCreationResponse?: CdrImageUploadLinkCreationResponse;
  uploading: boolean;
  uploadProgress?: number;
  uploadSuccess?: boolean;
  uploadError?: boolean;
  loadingDraft: boolean,
  deletingDraftFile: boolean,
  patientMedicalDocumentPdfResponse?: CdrDocumentFileContent,
  patientMedicalDocumentPdfModalOpen: boolean,
}

class AddEditPatientDocument extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      doctorOptions: [],
      documentTypeOptions: [],
      requiredDataLoaded: false,
      documentComments: [],
      currentUserDisplay: {
        id: -1,
        firstName: "",
        name: ""
      },
      addingComment: false,
      editingComments: false,
      initialFormValues: props.patientDocument ? props.patientDocument : this.initialDocumentValues(),
      showDeletedComments: false,
      uploading: false,
      loadingDraft: false,
      deletingDraftFile: false,
      patientMedicalDocumentPdfModalOpen: false,
      cancelTokenSource
    };

    this.fetchRequiredData(props);
  }

  initialDocumentValues = (): Partial<UpsertPatientMedicalDocumentDto> => {

    const {
      patientDocument,
      patientId,
      ownerEntityId,
      ownerEntity
    } = this.props;

    return {
      patientId: patientId,
      name: "",
      description: "",
      ownerEntityId,
      ownerEntity,
      documentDate: moment().format("YYYY-MM-DD"),
      requiresAction: true,
      comments: patientDocument ? patientDocument.comments : [],
      files: patientDocument ? patientDocument.files : []
    };
  };

  fetchRequiredData = async (props: Props) => {

    const {cancelTokenSource} = this.state;
    const {documentId, language} = this.props;

    const doctors = await getAllDoctors(cancelTokenSource);
    const doctorOptions: DropdownOption[] = mapToDropdownOptionArray(doctors, ["firstName", "name"], "id");

    const documentTypes = await getAllPatientDocumentTypes(cancelTokenSource);
    const documentTypeOptions = mapToDropdownOptionArrayWithTextGetter(
        documentTypes,
        (docType: PatientDocumentTypeDto) => getText(docType.description, language),
        "code", "code");

    let documentComments: Partial<PatientMedicalDocumentCommentDtoExtended>[] = [];
    if (documentId) {
      const documentCommentsResponse =
          await getPatientMedicalDocumentComments(documentId, true, cancelTokenSource);

      documentComments = prepareComments(documentCommentsResponse);
    }

    let currentUserDisplay = await getUserDisplay(cancelTokenSource);

    const draft = await createCdrDocumentDraft(this.props.patientId, cancelTokenSource);

    this.setState({
      doctorOptions,
      documentTypeOptions: documentTypeOptions,
      documentComments: documentComments,
      currentUserDisplay,
      draft,
      requiredDataLoaded: true
    });
  };

  isExistingFile = (file: any): file is PatientMedicalDocumentFileDto => {
    if ((file as PatientMedicalDocumentFileDto).documentId) {
      return true;
    }
    return false;
  };

  setPatientDocumentPdfModalOpen = async (
      file: CdrDocumentDraftFileDto | PatientMedicalDocumentFileDto) => {

    const {
      draft,
      cancelTokenSource
    } = this.state;

    const {
      patient,
      documentId
    } = this.props;

    const patientId = patient.id;

    try {
      let response: CdrDocumentFileContent | undefined = undefined;
      if (this.isExistingFile(file)) {
        response = await getCdrDocumentFile(
            patientId, documentId!, file.id, cancelTokenSource);
      } else {
        response = await getCdrDocumentDraftFile(
            patientId, draft!.id, file.id, cancelTokenSource);
      }

      this.setState({
        patientMedicalDocumentPdfResponse: response,
        patientMedicalDocumentPdfModalOpen: true
      });
    } catch (e) {
      e.response && this.handleError(e.response.data);
    }
  };

  updateDraftData = async () => {
    const {patient} = this.props;
    const {draft, cancelTokenSource} = this.state;

    if (!draft) {
      return;
    }

    this.setState({loadingDraft: true});

    try {
      const draftResponse =
          await getCdrDocumentDraft(patient.id, draft.id, cancelTokenSource);

      this.setState({
        draft: draftResponse
      });
    } catch (e) {

    } finally {
      this.setState({loadingDraft: false});
    }
  };

  showHideDeletedComments = async () => {
    const {showDeletedComments} = this.state;

    this.setState({
      showDeletedComments: !showDeletedComments
    });
  };

  handleSubmit = async (values: Partial<UpsertPatientMedicalDocumentDto>) => {

    const {
      documentComments,
      cancelTokenSource,
      draft
    } = this.state;

    const {
      patientId,
      documentId,
      onSave
    } = this.props;

    this.setState({
      savingDocument: true
    });

    const upsertComments = prepareUpsertCommentsDtos(documentComments);

    const request: Partial<UpsertPatientMedicalDocumentDto> = {
      ...values,
      draftId: draft!.id,
      comments: upsertComments
    };

    if (documentId) {
      await editCdrDocument(patientId, documentId, request, cancelTokenSource)
          .then((response) => {
            onSave(true, documentId);
          })
          .catch((e) => this.handleError(e.response.data));
    } else {
      await addCdrDocument(patientId, request, cancelTokenSource)
          .then((response) => onSave(true, documentId))
          .catch((e) => this.handleError(e.response.data));
    }

    this.setState({
      savingDocument: false
    });
  };

  handleFileUploadProgress = (progressEvent: any) => {
    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
    this.setState({uploadProgress: progress});
  };

  onDropAccepted = async (acceptedFiles: File[]) => {
    const {
      cancelTokenSource,
      draft
    } = this.state;

    const {patientId} = this.props;
    const draftId = draft ? draft.id : undefined;

    this.setState({uploading: true});

    if (draftId != undefined) {
      try {
        let draft = await addFilesToDraft(
            patientId,
            draftId,
            acceptedFiles,
            cancelTokenSource,
            this.handleFileUploadProgress
        );
        this.setState({
          draft,
          uploadSuccess: true
        });
      } catch (e) {
        this.setState({uploadError: true});
      }
    }

    this.setState({uploading: false});

    setTimeout(() => {
      this.setState({
        uploadProgress: undefined,
        uploadSuccess: undefined,
        uploadError: undefined
      });
    }, 1000);
  };

  onDropRejected = (rejections: FileRejection[]) => {
    if (rejections && rejections.length > 0 && rejections[0].errors) {
      const errors = rejections[0].errors;
      this.setErrorMessage(errors[0].message);
    }
  };

  removeNewUploadFile = async (form: FormApi, file: CdrDocumentDraftFileDto) => {
    const {patientId} = this.props;
    const {
      cancelTokenSource,
      draft
    } = this.state;
    this.setState({deletingDraftFile: true});
    try {
      let CdrDocumentDraftDto = await deleteCdrDocumentDraftFile(
          patientId,
          draft!.id,
          file.id,
          cancelTokenSource
      );
      this.setState({draft: CdrDocumentDraftDto});
    } catch (e) {

    }
    this.setState({deletingDraftFile: false});
  };

  removePreviousUploadFile = (
      form: FormApi,
      file: PatientMedicalDocumentFileDto,
      values: Partial<UpsertPatientMedicalDocumentDto>
  ) => {
    const documentFilesToBeRemoved = !!values.documentFilesToBeRemoved ? values.documentFilesToBeRemoved : [];
    form.change(
        "documentFilesToBeRemoved",
        [...documentFilesToBeRemoved, file.id]
    );
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`));
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`));
      } else {
        this.setErrorMessage(getMessage("error.general"));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    });
  };

  cancelHandler = async () => {
    const {patientId, onCancel} = this.props;
    const {cancelTokenSource, draft} = this.state;

    if (draft) {
      await deleteCdrDocumentDraft(patientId, draft.id, cancelTokenSource);
    }
    onCancel();
  };

  render(): React.ReactNode {
    const {initialFormValues} = this.state;
    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={initialFormValues}
            subscription={{pristine: true, submitting: true, values: true}}
            render={this.renderForm}
            mutators={{
              ...arrayMutators
            }}
        />
    );
  };

  setAddingComment = (addingComment: boolean) => {
    this.setState({
      addingComment: addingComment
    });
  };

  setEditingComments = (editingComments: boolean) => {
    this.setState({
      editingComments
    });
  };

  renderAddPatientDocumentSnapshotQrButton = () => {
    const {getMessage, patient} = this.props;
    const {cdrImageUploadLinkCreationResponse, draft, cancelTokenSource} = this.state;

    const qrcodeVisible = !!cdrImageUploadLinkCreationResponse;
    const url = cdrImageUploadLinkCreationResponse ? cdrImageUploadLinkCreationResponse.url : undefined;
    const qrcodeb64 = cdrImageUploadLinkCreationResponse ? cdrImageUploadLinkCreationResponse.imageB64 : undefined;
    const b64background = `url('data:image/png;base64,${qrcodeb64}')`;

    return (
        <>
          <QrcodeActionButton
              poperDependencies={[qrcodeVisible]}
              popperModifiers={{
                preventOverflow: {
                  boundary: document.getElementById("clinicalDataRecordMainContent")
                }
              }}
              renderContent={
                (toggle?: () => void) => (
                    <QrContentDiv>
                      <div className="employee-data">
                        {`${patient.firstName} ${patient.name}, ${isoToDisplayDate(patient.birthDate)}`}
                      </div>
                      {
                        qrcodeVisible
                            ? <a target="_blank"
                                 rel="noreferrer"
                                 href={url}
                                 title={getMessage("patientRecord.patientDocument.takeSnapshot")}>
                              <div className="qrcode-wrapper">
                                <div className="qrcode" style={{
                                  backgroundImage: b64background
                                }}>
                                  &nbsp;
                                </div>
                              </div>
                            </a>
                            :
                            <div>
                              <Loader active inline/>
                            </div>
                      }
                      <div className="actions-row">
                        <Button compact size="mini" onClick={() => {
                          if (toggle) {
                            toggle();
                          }
                          this.updateDraftData();
                        }}
                        >
                          {getMessage("button.close")}
                        </Button>
                      </div>
                      <div className="link-info">
                        <Icon name="info circle"/>
                        {getMessage("patientRecord.patientDocument.linkInfo")}
                      </div>
                    </QrContentDiv>
                )
              }
              onConfirm={() => {
              }}
              onOpen={() => {
                this.setState({
                  cdrImageUploadLinkCreationResponse: undefined
                });

                createCdrDocumentDraftBase64String(
                    patient.id,
                    draft!.id,
                    cancelTokenSource
                ).then((response: CdrImageUploadLinkCreationResponse | undefined) => {
                  this.setState({
                    cdrImageUploadLinkCreationResponse: response
                  });
                });
              }}
              position="left center"
              trigger={
                <StyledTriggerContainer>
                  <div>
                    {getMessage("patientRecord.patientDocument.uploadSnapshot")}
                  </div>
                  <Icon name={"camera"} color="grey" size="big"/>
                </StyledTriggerContainer>
              }
          />
        </>
    );
  };

  hidePatientDocumentPdfModal = () => {
    this.setState({
      patientMedicalDocumentPdfResponse: undefined,
      patientMedicalDocumentPdfModalOpen: false
    });
  };

  renderForm = (
      {form, handleSubmit, submitting, values}: FormRenderProps): React.ReactNode => {

    const {
      getMessage
    } = this.props;

    const {
      errorMessage,
      savingDocument,
      doctorOptions,
      documentTypeOptions,
      requiredDataLoaded,
      documentComments,
      currentUserDisplay,
      addingComment,
      editingComments,
      showDeletedComments,
      uploadProgress,
      uploadSuccess,
      uploadError,
      draft,
      uploading,
      loadingDraft,
      deletingDraftFile,
      patientMedicalDocumentPdfResponse,
      patientMedicalDocumentPdfModalOpen
    } = this.state;

    const patient = this.props.patient;
    const patientBirthDate = isoToDisplayDate(patient.birthDate);

    let newUploadFiles: CdrDocumentDraftFileDto[] = draft ? draft.files : [];
    let previousUploadFiles: PatientMedicalDocumentFileDto[] = values.documentFiles || [];

    previousUploadFiles = previousUploadFiles.filter(file => {
      const documentFilesToBeRemoved = !!values.documentFilesToBeRemoved ? [...values.documentFilesToBeRemoved] : [];
      return !documentFilesToBeRemoved.includes(file.id);
    });

    let documentFiles: (CdrDocumentDraftFileDto | PatientMedicalDocumentFileDto)[] =
        [...previousUploadFiles, ...newUploadFiles];

    let gender: string = "";

    switch (this.props.patient.sex) {
      case 0:
        gender = getMessage("gender.male");
        break;

      case 1:
        gender = getMessage("gender.female");
        break;

      case 2:
        gender = getMessage("gender.other");
        break;

      default:
        gender = "";
        break;
    }
    gender = gender.slice(0, 1).toLowerCase();

    const headerMessage = patient.firstName + " " + patient.name + ", " + patientBirthDate + ", " + gender;

    const draftChanging = loadingDraft || deletingDraftFile || uploading;

    const currentUser = this.props.currentUser!;

    const requiresDoctorActionCheckDisabled = this.props.documentId
        && this.props.patientDocument
        && this.props.patientDocument.requiresDoctorAction
        && !(
            currentUser.roles.includes("ROLE_APP_ADMIN")
            || this.props.currentUserHasPrivilege(Privilege.PX_CORE_CAN_VIEW_REQUIRES_DOCTOR_ACTION_NOTIFICATIONS)
        );

    if (!requiredDataLoaded) {
      <LoaderComponent message="loading"/>;
    }

    return <form onSubmit={handleSubmit}>
      <FormContainer>
        <FormContent>
          {errorMessage && <StyledErrorMessage
              onDismiss={() => this.setErrorMessage()}
          >
            {errorMessage}
          </StyledErrorMessage>}
          <PraxFormScrollableContainer height="600px">
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column>
                  <Header>
                    <Icon name="user"/>
                    {headerMessage}
                  </Header>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={2}>
                  {getMessage(
                      "patientRecord.patientDocument.uploadPatientDocument.documentName"
                  )}
                </Grid.Column>
                <Grid.Column width={6}>
                  <Field name="name" component={Input} fluid required/>
                </Grid.Column>
                <Grid.Column width={2}>
                  {getMessage(
                      "patientRecord.patientDocument.uploadPatientDocument.documentDescription"
                  )}
                </Grid.Column>
                <Grid.Column width={6}>
                  <Field name="description" component={Input} fluid required/>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={2}>
                  {getMessage(
                      "patientRecord.patientDocument.uploadPatientDocument.documentType"
                  )}
                </Grid.Column>
                <Grid.Column width={6}>
                  <Field name="type" component={Select} options={documentTypeOptions} fluid required/>
                </Grid.Column>
                <Grid.Column width={2}>
                  {getMessage(
                      "patientRecord.patientDocument.uploadPatientDocument.documentDate"
                  )}
                </Grid.Column>
                <Grid.Column width={6}>
                  <Field name="documentDate" component={DatePicker} fluid required/>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={2}>
                  {getMessage(
                      "patientRecord.patientDocument.uploadPatientDocument.doctor"
                  )}
                </Grid.Column>
                <Grid.Column width={6}>
                  <Field name="doctorId" component={Select} options={doctorOptions} clearable fluid/>
                </Grid.Column>
                <Grid.Column width={8} verticalAlign="middle">
                  <div>
                    <Field name="procedureCreated"
                           component={CheckBox}
                           label={getMessage("patientRecord.patientDocument.procedureCreated")}
                    />
                  </div>
                  <div>
                    <Field name="requiresAction"
                           component={CheckBox}
                           label={getMessage("patientRecord.patientDocument.requiresAction")}
                    />
                  </div>
                  <div>
                    <Field name="requiresDoctorAction"
                           disabled={requiresDoctorActionCheckDisabled}
                           component={CheckBox}
                           label={getMessage("patientRecord.patientDocument.requiresDoctorAction")}
                    />
                  </div>
                  <div>
                    <Field name="abnormalFinding"
                           component={CheckBox}
                           label={getMessage("patientRecord.patientDocument.abnormalFinding")}
                    />
                  </div>
                  <div>
                    <Field name="discussWithPatient"
                           component={CheckBox}
                           label={getMessage("patientRecord.patientDocument.discussWithPatient")}
                    />
                  </div>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={12} verticalAlign="middle">
                  <div>
                    {getMessage(
                        "patientRecord.patientDocument.uploadPatientDocument.file"
                    )}
                  </div>
                  <div>
                    <DropZone multiple={true} message={getMessage("patientDocument.upload")} maxSize={_100MB_in_bytes}
                              onDropAccepted={(acceptedFiles: File[]) => this.onDropAccepted(acceptedFiles)}
                              onDropRejected={this.onDropRejected}/>
                    {uploadProgress != undefined &&
                        <Progress percent={uploadProgress}
                                  attached="bottom"
                                  color="blue"
                                  success={uploadSuccess}
                                  error={uploadError}
                        />}
                  </div>
                </Grid.Column>
                <Grid.Column width={4} verticalAlign="middle">
                  <SnapshotButtonContainer>
                    {this.renderAddPatientDocumentSnapshotQrButton()}
                  </SnapshotButtonContainer>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <Button type="button" compact disabled={draftChanging || submitting} onClick={this.updateDraftData}>
                    <Icon name="refresh"/>
                    {getMessage("button.refresh")}
                  </Button>
                </Grid.Column>
              </Grid.Row>
              {documentFiles.map(
                  (
                      file: CdrDocumentDraftFileDto | PatientMedicalDocumentFileDto,
                      index: number
                  ) => {
                    return (
                        <FileName>
                          <div>{file.fileName}</div>
                          <FileActionButton>
                            <Button
                                icon
                                type="button"
                                onClick={() =>
                                    this.setPatientDocumentPdfModalOpen(file)
                                }
                                style={btnStyle}
                                disabled={draftChanging || submitting}
                            >
                              <Icon name="eye" size="small"/>
                            </Button>
                          </FileActionButton>
                          <FileActionButton>
                            <Button
                                icon
                                type="button"
                                onClick={() =>
                                    this.isExistingFile(file)
                                        ? this.removePreviousUploadFile(
                                            form,
                                            file,
                                            values
                                        )
                                        : this.removeNewUploadFile(form, file)
                                }
                                style={btnStyle}
                                disabled={draftChanging || submitting}
                            >
                              <Icon name="trash alternate" size="small"/>
                            </Button>
                          </FileActionButton>
                        </FileName>
                    );
                  }
              )}
              <Grid.Row>
                <Grid.Column width={12} verticalAlign="middle">
                  <StyledSpan>
                    {getMessage("patientRecord.patientDocument.uploadPatientDocument.comment")}
                    <div className="show-hide-comments" onClick={() => this.showHideDeletedComments()}>
                      {showDeletedComments
                          ? <u>{getMessage("patientRecord.patientDocument.hideDeletedComments")}</u>
                          : <u>{getMessage("patientRecord.patientDocument.showDeletedComments")}</u>
                      }
                    </div>
                  </StyledSpan>

                  <PatientDocumentComments comments={documentComments} currentUserDisplay={currentUserDisplay}
                                           setAddingComment={this.setAddingComment}
                                           setEditingComments={this.setEditingComments}
                                           showDeletedComments={showDeletedComments}
                                           onChange={(comments: UpsertPatientMedicalDocumentCommentDto[]) => {
                                             this.setState({documentComments: comments});
                                           }}/>
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
          </PraxFormScrollableContainer>

          {savingDocument && <LoaderComponent message={getMessage("patientRecord.patientDocument.loading.message")}/>}

          <FormButtonsRow>
            <Button type="button" compact primary
                    disabled={submitting || editingComments || addingComment || draftChanging}
                    onClick={(evt) => handleSubmit(evt as any)}
            >
              {getMessage("button.save")}
            </Button>

            <Button type="button" compact disabled={submitting} onClick={() => this.cancelHandler()}>
              {getMessage("button.cancel")}
            </Button>
          </FormButtonsRow>
        </FormContent>

        {patientMedicalDocumentPdfModalOpen && <PatientMedicalDocumentPdfModal
            pdfResponses={patientMedicalDocumentPdfResponse ? [patientMedicalDocumentPdfResponse] : undefined}
            modalOpen={patientMedicalDocumentPdfModalOpen} onCloseModal={this.hidePatientDocumentPdfModal}
            getPdfUrl={getPatientDocumentFileUrl} textContent={""}
            comments={prepareComments(patientMedicalDocumentPdfResponse!.comments || [])}
            currentUserDisplay={this.state.currentUserDisplay} patientId={patient.id} documentId={undefined}
            showSetControlledButton={false} draftMode={true} successfullyChangedReportStatus={() => {
        }}/>}
      </FormContainer>
    </form>;
  };
}

export default withAuthContext(
    withMessages(
        AddEditPatientDocument));
